<template>
  <div>
    <div v-if="!message.user" class="chat-widget__message-sender">
      <div class="chat-message-box">
        <img
          alt="Profile Image"
          class="chat-profile-image"
          :src="otherProfileImage"
        />
        <span
          :class="{
            'chat-widget__message-bubble': true,
            'chat-widget__message-bubble--other': true,
            'chat-widget__message-bubble--streaming':
              isLastMessage && isStreaming,
          }"
        >
          <vue-markdown
            class="chat-widget__message-text"
            :source="sanitizedMessageText"
          ></vue-markdown>
          <router-link v-if="message.link" :to="message.link">
            <span v-text="message.linkText"></span>
          </router-link>
          <span
            v-if="isLastMessage && isStreaming"
            class="chat-widget__cursor"
          ></span>
        </span>
        <message-reaction v-if="message.id" :message="message" />
      </div>
      <citations v-if="message.citations" :citations="message.citations" />
    </div>
    <span v-if="message.user" class="chat-widget__message-bubble">
      <span v-text="message.text"></span>
    </span>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MessageReaction from './MessageReaction.vue';
import Citations from './Citations.vue';
import { COPILOT_GETTERS } from '../../store/modules/copilot';
import VueMarkdown from 'vue-markdown';
import DOMPurify from 'dompurify';

export default {
  name: 'ChatMessage',
  components: {
    MessageReaction,
    Citations,
    VueMarkdown,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    otherProfileImage: {
      type: String,
      required: true,
    },
    isLastMessage: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('copilot', {
      isStreaming: COPILOT_GETTERS.isStreaming,
    }),
    sanitizedMessageText() {
      return DOMPurify.sanitize(this.message.text);
    },
  },
  mounted() {
    this.forceExternalLinksToNewTab();
  },
  updated() {
    this.forceExternalLinksToNewTab();
  },
  methods: {
    forceExternalLinksToNewTab() {
      // Markdown-rendered download links (e.g. CSV + baseline-loan JSON for
      // `/rule-test-z3`) point at S3-signed URLs. Without `target="_blank"`
      // the user navigates away from the chat widget and loses session state.
      if (!this.$el || !this.$el.querySelectorAll) return;
      const links = this.$el.querySelectorAll('.chat-widget__message-text a[href]');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-widget__message-bubble--streaming {
  position: relative;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-widget__cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background-color: currentColor;
  margin-left: 2px;
  animation: blink 1s step-end infinite;
  vertical-align: text-bottom;
  opacity: 0.5;
}

@keyframes blink {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0;
  }
}

.chat-widget__message-text {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

::v-deep .chat-widget__message-text p,
::v-deep .chat-widget__message-text h2,
::v-deep .chat-widget__message-text h3,
::v-deep .chat-widget__message-text h4 {
  margin-bottom: 0;
}

::v-deep .chat-widget__message-text ul {
  display: flex;
  flex-direction: column;
}

::v-deep .chat-widget__message-text li {
  white-space: normal;
}
</style>
